/**
 * AI Assistant Action Types
 * Constants for AI assistant functionality across the application
 */

export const AI_ASSISTANT_ACTIONS = {
    SUGGEST: 'suggest',
    GRAMMAR: 'grammar',
    TRANSLATE: 'translate',
    SUMMARIZE: 'summarize',
    IMPROVE: 'improve',
    VALIDATE_FORMAT: 'validate_format',
    ENHANCE: 'enhance',
    CUSTOM: 'custom',
    ENTITY_SUMMARY: 'entity_summary',
    PLAN_NEXT_STEPS: 'plan_next_steps',
    RECENT_UPDATES: 'recent_updates',
} as const;

export type AiAssistantAction = (typeof AI_ASSISTANT_ACTIONS)[keyof typeof AI_ASSISTANT_ACTIONS];

/**
 * Array of all AI assistant action keys for easy iteration
 */
export const AI_ASSISTANT_ACTION_KEYS = Object.values(AI_ASSISTANT_ACTIONS);

/** Provider values recorded by LLM observability across chat, embedding, and reranking. */
export const AI_OBSERVABILITY_PROVIDERS = ['OPENAI', 'GOOGLE', 'ANTHROPIC', 'ZEROENTROPY', 'COHERE'] as const;

/** AI provider options for selection in forms and displays. */
export const AI_PROVIDERS = [
    { label: 'OpenAI', value: 'OPENAI' },
    { label: 'Google', value: 'GOOGLE' },
    { label: 'Anthropic', value: 'ANTHROPIC' },
    { label: 'ZeroEntropy', value: 'ZEROENTROPY' },
    { label: 'Cohere', value: 'COHERE' },
];

/**
 * AI Prompt Provider values as tuple for Zod enum validation.
 *
 * Mirrors the full `AiPromptProvider` Prisma enum — AiPrompt records span chat
 * and embedding categories, and ZEROENTROPY is a valid embedding provider, so
 * the validation tuple must match the column. COHERE exists in the column only
 * for AiModelPricing (rerank cost rows) — like ZEROENTROPY it is excluded from
 * the chat-prompt editor's provider dropdown (see
 * client/src/components/ai-prompts/constants.ts), and chat/LLM execution
 * rejects both in getAiSdkModel.
 */
export const AI_PROMPT_PROVIDERS = AI_OBSERVABILITY_PROVIDERS;
export type AiPromptProviderType = (typeof AI_PROMPT_PROVIDERS)[number];

/** Maximum number of ordered fallback models configured for a model tier. */
export const MAX_AI_MODEL_TIER_FALLBACKS = 3;

/**
 * Reasoning effort levels for prompts and providers, ordered cheapest → most thorough.
 *
 * Mirrors the ladder the installed `@ai-sdk/openai` accepts for `providerOptions.openai
 * .reasoningEffort` (GPT-5.6 and friends document `none, low, medium, high, xhigh, max`).
 * `none` is deliberately absent: it is the one level a chunk of the fleet rejects outright
 * (gpt-5-nano 400s on it), and "no reasoning at all" is already expressible via a
 * non-reasoning tier. Not every model accepts every level either — gpt-5.4-mini rejects
 * `minimal`. The OpenAI provider boundary handles the new top rungs model-by-model; other
 * compatibility errors continue to surface from the provider.
 */
export const REASONING_EFFORTS = {
    MINIMAL: 'minimal',
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    XHIGH: 'xhigh',
    MAX: 'max',
} as const;

export type ReasoningEffort = (typeof REASONING_EFFORTS)[keyof typeof REASONING_EFFORTS];

/**
 * Resolve the shared effort ladder for one OpenAI model attempt.
 *
 * GPT-5.6-class models accept `xhigh` and `max`. Older reasoning families retain the
 * operator's intent at their highest supported rung (`high`), while non-reasoning models drop
 * top-rung effort entirely. This resolver belongs at the attempt boundary because a single tier
 * setting can reach several differently capable fallback models.
 */
export function resolveOpenAiReasoningEffort(
    model: string,
    effort: ReasoningEffort | null | undefined,
): ReasoningEffort | undefined {
    if (effort === null || effort === undefined) return undefined;

    const isTopRung = effort === REASONING_EFFORTS.XHIGH || effort === REASONING_EFFORTS.MAX;
    if (!isTopRung || /^gpt-5\.6(?:$|-)/i.test(model)) return effort;

    if (/^(?:o\d|gpt-5)/i.test(model)) return REASONING_EFFORTS.HIGH;
    return undefined;
}

/**
 * Operator-facing labels for the effort ladder. Derived labels ('XHIGH' → 'Xhigh') read as
 * typos, so the display text is spelled out here and shared by every effort dropdown.
 *
 * Labels stay neutral about what "default" means — the two dropdowns disagree. The prompt
 * editor defaults to 'medium'; the model-tier drawer treats a CLEARED value as "inherit the
 * tier default", so tagging 'medium' as the default there would be wrong. A surface that has
 * a default annotates its own option.
 */
const REASONING_EFFORT_LABELS = {
    minimal: 'Minimal',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    xhigh: 'Extra high',
    max: 'Max',
} satisfies Record<ReasoningEffort, string>;

export const REASONING_EFFORT_OPTIONS = Object.values(REASONING_EFFORTS).map((value) => ({
    label: REASONING_EFFORT_LABELS[value],
    value,
}));

/** Type guard for values arriving from the DB column / imported JSON, which are plain strings. */
export function isReasoningEffort(value: unknown): value is ReasoningEffort {
    return typeof value === 'string' && (Object.values(REASONING_EFFORTS) as string[]).includes(value);
}

export { REASONING_EFFORTS as REASONING_EFFORTS_EXPORT_GUARD };

export const AI_OUTPUT_FORMATS = {
    TEXT: 'text',
    MARKDOWN: 'markdown',
    HTML: 'html',
} as const;

export type AiOutputFormat = (typeof AI_OUTPUT_FORMATS)[keyof typeof AI_OUTPUT_FORMATS];

export const AI_OUTPUT_FORMAT_VALUES = Object.values(AI_OUTPUT_FORMATS);

/**
 * AI Model Tiers
 * Capability-based tiers for different AI use cases
 */
export const AI_MODEL_TIERS = [
    'NANO',
    'LIGHTWEIGHT',
    'BALANCED',
    'EXTRACTION',
    'DOCUMENT_EXTRACTOR',
    'REASONING',
    'PREMIUM',
    'EMBEDDING_SMALL',
    'EMBEDDING_LARGE',
] as const;

export type AiModelTier = (typeof AI_MODEL_TIERS)[number];

/**
 * Metadata for each AI model tier including display label, description, and category
 */
export const AI_MODEL_TIER_METADATA: Record<
    AiModelTier,
    { label: string; description: string; category: 'text' | 'embedding' }
> = {
    NANO: {
        label: 'Nano',
        description: 'Ultra-cheap minimal processing tasks',
        category: 'text',
    },
    LIGHTWEIGHT: {
        label: 'Lightweight',
        description: 'Quick, cost-effective tasks',
        category: 'text',
    },
    BALANCED: {
        label: 'Balanced',
        description: 'Standard quality for most tasks',
        category: 'text',
    },
    EXTRACTION: {
        label: 'Extraction',
        description: 'Cost-effective document metadata extraction',
        category: 'text',
    },
    DOCUMENT_EXTRACTOR: {
        label: 'Document Extractor',
        description: 'Document to markdown conversion using vision models',
        category: 'text',
    },
    REASONING: {
        label: 'Reasoning',
        description: 'Complex analysis and decision-making',
        category: 'text',
    },
    PREMIUM: {
        label: 'Premium',
        description: 'Maximum quality for critical tasks',
        category: 'text',
    },
    EMBEDDING_SMALL: {
        label: 'Embedding (Small)',
        description: 'Cost-effective vector search',
        category: 'embedding',
    },
    EMBEDDING_LARGE: {
        label: 'Embedding (Large)',
        description: 'High-fidelity document analysis',
        category: 'embedding',
    },
} as const;

/**
 * Provider color mappings for UI display
 */
export const AI_PROVIDER_COLORS: Record<string, string> = {
    OPENAI: 'blue',
    GOOGLE: 'green',
    ANTHROPIC: 'purple',
    ZEROENTROPY: 'orange',
    COHERE: 'geekblue',
} as const;
